import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    setNotFound(false);
    setData(null);
    api.get(`/projects/public/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-32 max-w-[1200px] mx-auto px-6 py-20" data-testid="project-not-found">
        <h1 className="font-heading text-4xl">Project not found</h1>
        <Link to="/projects" className="mt-6 inline-block border-b border-ink text-sm">Back to projects</Link>
      </div>
    );
  }
  if (!data) return <div className="pt-40 text-center text-stone font-mono text-sm">Loading…</div>;

  const p = data.project;
  const gallery = p.gallery || [];
  const current = gallery[heroIdx] || p.cover_image_url;

  return (
    <div className="pt-24" data-testid="project-detail">
      {/* Cover */}
      <section className="relative w-full h-[75vh] min-h-[560px] bg-ink overflow-hidden">
        <img src={mediaUrl(current)} alt={p.name} className="w-full h-full object-cover opacity-95" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-14 w-full">
            <div className="eyebrow text-white/70 mb-3">{p.project_type} · {p.status}</div>
            <h1 className="font-heading font-light text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              {p.name}
            </h1>
            <div className="text-white/85 mt-4 text-sm">{p.location} · {p.year}</div>
          </div>
        </div>
      </section>

      {/* Facts + description */}
      <section className="py-24 border-b border-line">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-3">Project facts</div>
            <dl className="space-y-3">
              <div><dt className="text-xs text-stone">Location</dt><dd className="text-base">{p.location}</dd></div>
              <div><dt className="text-xs text-stone">Year</dt><dd className="text-base">{p.year || "—"}</dd></div>
              <div><dt className="text-xs text-stone">Status</dt><dd className="text-base">{p.status}</dd></div>
              <div><dt className="text-xs text-stone">Services</dt><dd className="text-base">{(p.services || []).join(" · ")}</dd></div>
              {Object.entries(p.facts || {}).map(([k, v]) => (
                <div key={k}><dt className="text-xs text-stone">{k}</dt><dd className="text-base">{v}</dd></div>
              ))}
            </dl>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="eyebrow mb-3">About the project</div>
            <p className="font-heading font-light text-2xl sm:text-3xl leading-snug tracking-tight text-ink">
              {p.description}
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 1 && (
        <section className="py-24">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="eyebrow mb-6">Gallery · {gallery.length} images</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIdx(i)}
                  className="zoom-wrap bg-sand aspect-[4/3] block"
                  data-testid={`gallery-item-${i}`}
                >
                  <img src={mediaUrl(src)} alt={`${p.name} ${i + 1}`} className="zoom-img w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Before / After */}
      {(p.before_images || []).length > 0 && (p.after_images || []).length > 0 && (
        <section className="py-24 bg-paper-warm border-t border-line">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="eyebrow mb-6">Before &amp; After</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {p.before_images.slice(0, 1).map((src, i) => (
                <figure key={`b-${i}`}>
                  <img src={mediaUrl(src)} alt="Before" className="w-full aspect-[4/3] object-cover" />
                  <figcaption className="eyebrow mt-3">Before</figcaption>
                </figure>
              ))}
              {p.after_images.slice(0, 1).map((src, i) => (
                <figure key={`a-${i}`}>
                  <img src={mediaUrl(src)} alt="After" className="w-full aspect-[4/3] object-cover" />
                  <figcaption className="eyebrow mt-3">After</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {(data.related || []).length > 0 && (
        <section className="py-24 border-t border-line">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="eyebrow mb-6">Related projects</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.related.map((r) => (
                <Link key={r.id} to={`/projects/${r.id}`} className="group block">
                  <div className="zoom-wrap bg-sand aspect-[4/3]">
                    <img src={mediaUrl(r.cover_image_url)} alt={r.name} className="zoom-img w-full h-full object-cover" />
                  </div>
                  <div className="mt-3 font-heading text-xl">{r.name}</div>
                  <div className="text-sm text-stone">{r.location} · {r.year}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-ink text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="font-heading font-light text-3xl sm:text-4xl tracking-tight max-w-[22ch]">
            Considering a similar project? Let's talk about it.
          </div>
          <Link to="/contact" data-testid="project-cta-btn" className="inline-flex items-center gap-3 bg-white text-ink px-7 py-3.5 text-sm">
            Start a conversation <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <Link to="/projects" className="inline-flex items-center gap-2 text-sm border-b border-ink pb-1" data-testid="back-to-projects">
          <ArrowLeft size={14} /> All projects
        </Link>
      </div>
    </div>
  );
}
